// src/stores/session.ts
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { auth, db } from '../lib/firebase';

// Runtime imports
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';

// Type aliases (no runtime imports)
type FirebaseUser = import('firebase/auth').User;
type Unsubscribe = () => void;

import type { Restaurant } from '../types';

type AuthState = 'initializing' | 'authenticated' | 'unauthenticated' | 'transitioning';

const ACTIVE_KEY = 'activeRestaurantId';

export const useSessionStore = defineStore('session', () => {
  const user = ref<FirebaseUser | null>(null);
  const currentRestaurantId = ref<string | null>(null);
  const currentRestaurant = ref<Restaurant | null>(null);
  const authState = ref<AuthState>('initializing');
  const redirectPath = ref<string | null>(null);
  const restaurantLoading = ref(false);

  let restaurantUnsubscribe: Unsubscribe | null = null;
  let authUnsubscribe: Unsubscribe | null = null;

  // Computed values
  const isInitialized = computed(() => authState.value !== 'initializing');
  const isAuthenticated = computed(() => authState.value === 'authenticated');
  const isOnboarded = computed(() => isAuthenticated.value && !!currentRestaurantId.value);
  const isRestaurantLoaded = computed(() => isAuthenticated.value && !restaurantLoading.value);
  const isTransitioning = computed(() => authState.value === 'transitioning');
  const currentUserRole = computed(() => {
    if (!user.value || !currentRestaurant.value?.members) {
      console.log('[session] currentUserRole: no user or members', {
        hasUser: !!user.value,
        hasRestaurant: !!currentRestaurant.value,
        hasMembers: !!currentRestaurant.value?.members
      });
      return null;
    }
    const role = currentRestaurant.value.members[user.value.uid] || null;
    console.log('[session] currentUserRole:', {
      uid: user.value.uid,
      role,
      members: currentRestaurant.value.members
    });
    return role;
  });
  const isOwner = computed(() => {
    const result = currentUserRole.value === 'owner';
    console.log('[session] isOwner:', { role: currentUserRole.value, isOwner: result });
    return result;
  });
  const isStaff = computed(() => {
    const result = currentUserRole.value === 'staff';
    console.log('[session] isStaff:', { role: currentUserRole.value, isStaff: result });
    return result;
  });

  function stopRestaurantListener() {
    if (restaurantUnsubscribe) {
      restaurantUnsubscribe();
      restaurantUnsubscribe = null;
    }
  }

  /** Attach a live listener to a specific restaurant doc */
  function listenToRestaurant(restaurantId: string) {
    stopRestaurantListener();
    const dref = doc(db, 'restaurants', restaurantId);
    restaurantUnsubscribe = onSnapshot(
      dref,
      (snap) => {
        if (!snap.exists()) {
          currentRestaurantId.value = null;
          currentRestaurant.value = null;
          localStorage.removeItem(ACTIVE_KEY);
          return;
        }
        const data = snap.data() as any;
        if (user.value && Array.isArray(data.memberUids) && data.memberUids.includes(user.value.uid)) {
          currentRestaurantId.value = snap.id;
          currentRestaurant.value = { id: snap.id, ...data } as Restaurant;
          localStorage.setItem(ACTIVE_KEY, snap.id);
        } else {
          currentRestaurantId.value = null;
          currentRestaurant.value = null;
          localStorage.removeItem(ACTIVE_KEY);
        }
      },
      (err) => {
        console.error('[session] Restaurant listener error:', err);
      }
    );
  }

  /** Find the first restaurant where user is a member */
  async function loadUserRestaurant(uid: string) {
    try {
      // First try the hinted restaurant from localStorage
      const hintedId = localStorage.getItem(ACTIVE_KEY);
      if (hintedId) {
        const snap = await getDoc(doc(db, 'restaurants', hintedId));
        if (snap.exists()) {
          const data = snap.data() as any;
          if (Array.isArray(data.memberUids) && data.memberUids.includes(uid)) {
            currentRestaurantId.value = hintedId;
            currentRestaurant.value = { id: hintedId, ...data } as Restaurant;
            localStorage.setItem(ACTIVE_KEY, hintedId);
            restaurantLoading.value = false;
            listenToRestaurant(hintedId);
            return;
          }
        }
        localStorage.removeItem(ACTIVE_KEY);
      }

      // Fallback: query for first restaurant where user is member
      const q = query(
        collection(db, 'restaurants'),
        where('memberUids', 'array-contains', uid),
        limit(1)
      );

      restaurantUnsubscribe = onSnapshot(q, (snap) => {
        if (!snap.empty) {
          const d = snap.docs[0];
          currentRestaurantId.value = d.id;
          currentRestaurant.value = { id: d.id, ...d.data() } as Restaurant;
          localStorage.setItem(ACTIVE_KEY, d.id);
          restaurantLoading.value = false;
          // Switch to document listener for real-time updates
          listenToRestaurant(d.id);
        } else {
          currentRestaurantId.value = null;
          currentRestaurant.value = null;
          localStorage.removeItem(ACTIVE_KEY);
          restaurantLoading.value = false;
        }
      });
    } catch (error) {
      console.error('[session] Error loading user restaurant:', error);
      restaurantLoading.value = false;
    }
  }

  /** Handle user authentication state changes */
  function handleAuthChange(firebaseUser: FirebaseUser | null) {
    user.value = firebaseUser;

    if (firebaseUser) {
      // Only change to authenticated if not already transitioning (logout in progress)
      if (authState.value !== 'transitioning') {
        authState.value = 'authenticated';
      }
      restaurantLoading.value = true;
      loadUserRestaurant(firebaseUser.uid);
    } else {
      authState.value = 'unauthenticated';
      restaurantLoading.value = false;
      currentRestaurantId.value = null;
      currentRestaurant.value = null;
      stopRestaurantListener();
      localStorage.removeItem(ACTIVE_KEY);
    }
  }

  /** Initialize authentication listener */
  function initialize() {
    if (authUnsubscribe) {
      console.warn('[session] Already initialized');
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      authUnsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
        handleAuthChange(firebaseUser);
        resolve();
      });
    });
  }

  /** Set redirect path for post-auth navigation */
  function setRedirectPath(path: string) {
    redirectPath.value = path;
  }

  /** Get and clear redirect path */
  function getAndClearRedirectPath(): string | null {
    const path = redirectPath.value;
    redirectPath.value = null;
    return path;
  }

  /** Cleanup all listeners */
  function cleanup() {
    if (authUnsubscribe) {
      authUnsubscribe();
      authUnsubscribe = null;
    }
    stopRestaurantListener();
  }

  async function reloadRestaurant() {
    if (!currentRestaurantId.value) return;
    const d = await getDoc(doc(db, 'restaurants', currentRestaurantId.value));
    if (d.exists()) {
      currentRestaurant.value = { id: d.id, ...d.data() } as Restaurant;
      localStorage.setItem(ACTIVE_KEY, d.id);
    }
  }

  /** Start logout transition */
  function logout() {
    authState.value = 'transitioning';
  }

  /** Switch to a different restaurant/venue */
  function switchRestaurant(restaurantId: string) {
    if (restaurantId === currentRestaurantId.value) return;
    
    // Update localStorage
    localStorage.setItem(ACTIVE_KEY, restaurantId);
    
    // Stop current listener and start new one
    listenToRestaurant(restaurantId);
  }

  return {
    // State
    user,
    currentRestaurantId,
    currentRestaurant,
    authState,
    redirectPath,

    // Computed
    isInitialized,
    isAuthenticated,
    isOnboarded,
    isRestaurantLoaded,
    isTransitioning,
    currentUserRole,
    isOwner,
    isStaff,

    // Methods
    initialize,
    setRedirectPath,
    getAndClearRedirectPath,
    reloadRestaurant,
    switchRestaurant,
    logout,
    cleanup,
  };
});
